import { inferEnrollmentSourceFromRules, TicketAudience, TicketPriority, TICKET_AUDIENCE_LABELS } from '@21k/shared';
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  createTicket,
  getApiErrorMessage,
  listAssignableMembers,
  listChannelTypeOptions,
  listDepartments,
  listEnrollmentSourceRules,
  listTicketCategoryOptions,
  resolveIntakeRoute,
  searchEntityByEnrollment,
  searchEntityLearners,
  searchEntityParents,
  uploadAssets,
} from '../../api/client';
import type {
  AssignableTeamGroup,
  ChannelTypeOption,
  DepartmentSummary,
  EnrollmentLookupResult,
  EnrollmentSourceRule,
  EntityLearnerResult,
  EntityParentResult,
  LinkedSource,
  TicketCategoryRef,
} from '../../api/types';
import Badge from '../../components/Badge';
import AttachmentPicker from '../../components/AttachmentPicker';
import { useAuth } from '../../contexts/AuthContext';
import { getEmbedChannelCode, getEmbedSource } from '../../utils/embed';
import { sourceBadgeClass } from '../../utils/linkedSource';

type AssignmentMode = 'auto' | 'manual';
type LinkSearchMode = 'parent' | 'enrollment';

export default function CreateTicketPage() {
  const navigate = useNavigate();
  const submittingRef = useRef(false);
  const { user } = useAuth();
  const embedSource = getEmbedSource();
  const embedChannelCode = getEmbedChannelCode();
  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
  const [channelTypes, setChannelTypes] = useState<ChannelTypeOption[]>([]);
  const hostChannelId = useMemo(
    () => channelTypes.find((t) => t.code === embedChannelCode)?.id,
    [channelTypes, embedChannelCode],
  );
  const hideHostFields = Boolean(embedSource && hostChannelId);
  const hideLinkedProfile = embedSource === 'ERP';
  const [categories, setCategories] = useState<TicketCategoryRef[]>([]);
  const [assignableTeams, setAssignableTeams] = useState<AssignableTeamGroup[]>([]);

  const [departmentId, setDepartmentId] = useState('');
  const [routedDepartmentName, setRoutedDepartmentName] = useState('');
  const audience = TicketAudience.INTERNAL;
  const [categoryId, setCategoryId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [channelTypeId, setChannelTypeId] = useState('');
  const [linkedSource, setLinkedSource] = useState<LinkedSource>('ERP');
  const [linkSearchMode, setLinkSearchMode] = useState<LinkSearchMode>('parent');

  const [parentQuery, setParentQuery] = useState('');
  const [parentResults, setParentResults] = useState<EntityParentResult[]>([]);
  const [selectedParent, setSelectedParent] = useState<EntityParentResult | null>(null);
  const [learners, setLearners] = useState<EntityLearnerResult[]>([]);
  const [selectedLearner, setSelectedLearner] = useState<EntityLearnerResult | null>(null);
  const [learnerLocked, setLearnerLocked] = useState(false);

  const [enrollmentQuery, setEnrollmentQuery] = useState('');
  const [enrollmentResults, setEnrollmentResults] = useState<EnrollmentLookupResult[]>([]);
  const [searchingEnrollment, setSearchingEnrollment] = useState(false);
  const [enrollmentRules, setEnrollmentRules] = useState<EnrollmentSourceRule[]>([]);
  const [enrollmentHint, setEnrollmentHint] = useState('');
  const [sourceOptions, setSourceOptions] = useState<
    Array<{ source: LinkedSource; label: string; prefixes: string[] }>
  >([]);

  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');

  const [assignmentExpanded, setAssignmentExpanded] = useState(false);
  const [assignmentMode, setAssignmentMode] = useState<AssignmentMode>('auto');
  const [assignedToId, setAssignedToId] = useState('');
  const [teamId, setTeamId] = useState('');

  const [searchingParents, setSearchingParents] = useState(false);
  const [loadingLearners, setLoadingLearners] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      listDepartments(),
      listChannelTypeOptions(),
      listTicketCategoryOptions(),
      listEnrollmentSourceRules(),
    ])
      .then(([depts, types, cats, enrollmentConfig]) => {
        setDepartments(depts);
        setChannelTypes(types);
        setCategories(cats);
        setEnrollmentRules(enrollmentConfig.rules);
        setEnrollmentHint(enrollmentConfig.hint);
        setSourceOptions(
          enrollmentConfig.sources.map((s) => ({
            ...s,
            prefixes:
              enrollmentConfig.rules.find((r) => r.source === s.source)?.prefixes ?? [],
          })),
        );
        const hostChannel = embedChannelCode
          ? types.find((t) => t.code === embedChannelCode)
          : undefined;
        const manual = types.find((t) => t.code === 'MANUAL');
        setChannelTypeId(hostChannel?.id ?? manual?.id ?? types[0]?.id ?? '');
        if (embedSource) setLinkedSource(embedSource);
        if (cats[0]) setCategoryId(cats[0].id);
      })
      .catch((err) => setError(getApiErrorMessage(err)));
  }, [embedChannelCode, embedSource]);

  useEffect(() => {
    if (!channelTypeId || !categoryId) return;
    void resolveIntakeRoute({
      channelTypeId,
      categoryId,
      audience,
    })
      .then((resolved) => {
        setDepartmentId(resolved.departmentId);
        setRoutedDepartmentName(resolved.departmentName);
        setError((current) =>
          current === 'No routing rule matches this channel. Add one in Platform Config → Routing.'
            ? ''
            : current,
        );
      })
      .catch(() => {
        setDepartmentId('');
        setRoutedDepartmentName('');
        setError('No routing rule matches this channel. Add one in Platform Config → Routing.');
      });
  }, [channelTypeId, audience, categoryId]);

  const loadAssignable = useCallback(async (deptId: string) => {
    if (!deptId) {
      setAssignableTeams([]);
      return;
    }
    try {
      setAssignableTeams(await listAssignableMembers(deptId));
    } catch {
      setAssignableTeams([]);
    }
  }, []);

  useEffect(() => {
    void loadAssignable(departmentId);
    setAssignedToId('');
    setTeamId('');
    setAssignmentMode('auto');
  }, [departmentId, loadAssignable]);

  async function handleParentSearch() {
    if (!parentQuery.trim()) return;
    setSearchingParents(true);
    setError('');
    try {
      const res = await searchEntityParents(parentQuery.trim(), linkedSource);
      setParentResults(res.results);
      if (res.error === 'source_unavailable') {
        setError(`${linkedSource} database is unavailable — try another source or contact only.`);
      }
    } catch (err) {
      setError(getApiErrorMessage(err));
      setParentResults([]);
    } finally {
      setSearchingParents(false);
    }
  }

  async function selectParent(parent: EntityParentResult) {
    setSelectedParent(parent);
    setParentResults([]);
    setParentQuery('');
    setContactName(parent.displayName);
    setSelectedLearner(null);
    setLearnerLocked(false);
    setLoadingLearners(true);
    try {
      const res = await searchEntityLearners(parent.sourceParentId, linkedSource);
      setLearners(res.results);
      if (res.results.length === 1) {
        setSelectedLearner(res.results[0]!);
        setLearnerLocked(true);
      }
    } catch (err) {
      setError(getApiErrorMessage(err));
      setLearners([]);
    } finally {
      setLoadingLearners(false);
    }
  }

  function clearParentSelection() {
    setSelectedParent(null);
    setSelectedLearner(null);
    setLearners([]);
    setLearnerLocked(false);
    setEnrollmentResults([]);
    setEnrollmentQuery('');
  }

  function selectEnrollmentMatch(match: EnrollmentLookupResult) {
    setLinkedSource(match.source);
    setSelectedParent(match.parent);
    setSelectedLearner(match.learner);
    setLearners([match.learner]);
    setLearnerLocked(true);
    setEnrollmentResults([]);
    setEnrollmentQuery(match.enrollmentNumber);
    setContactName(match.parent.displayName);
    setParentQuery('');
    setParentResults([]);
  }

  async function handleEnrollmentSearch() {
    if (enrollmentQuery.trim().length < 3) return;
    setSearchingEnrollment(true);
    setError('');
    try {
      const res = await searchEntityByEnrollment(enrollmentQuery.trim(), linkedSource);
      setEnrollmentResults(res.results);
      if (res.error === 'source_unavailable') {
        setError('One or more source databases are unavailable — try another source.');
      } else if (res.results.length === 0) {
        setError(`No learner found in ${linkedSource} for that enrollment number.`);
      }
    } catch (err) {
      setError(getApiErrorMessage(err));
      setEnrollmentResults([]);
    } finally {
      setSearchingEnrollment(false);
    }
  }

  function handleLinkSearchModeChange(mode: LinkSearchMode) {
    setLinkSearchMode(mode);
    clearParentSelection();
    setParentQuery('');
    setParentResults([]);
    setError('');
  }

  function handleSourceChange(source: LinkedSource) {
    setLinkedSource(source);
    if (selectedParent) {
      clearParentSelection();
      setParentQuery('');
      setParentResults([]);
    } else {
      setEnrollmentResults([]);
    }
  }

  const routedDepartmentLabel = useMemo(() => {
    if (routedDepartmentName.trim()) return routedDepartmentName;
    return departments.find((d) => d.id === departmentId)?.name ?? '';
  }, [routedDepartmentName, departments, departmentId]);

  const manualAssignmentValue = useMemo(() => {
    if (!assignedToId || !teamId) return '';
    return `${teamId}:${assignedToId}`;
  }, [assignedToId, teamId]);

  function handleManualAssignmentChange(value: string) {
    if (!value) {
      setAssignmentMode('auto');
      setAssignedToId('');
      setTeamId('');
      return;
    }
    const [tId, uId] = value.split(':');
    setAssignmentMode('manual');
    setTeamId(tId ?? '');
    setAssignedToId(uId ?? '');
  }

  function assignToSelf() {
    if (!user) return;
    const myTeam = assignableTeams.find((t) =>
      t.members.some((m) => m.userId === user.id),
    );
    if (!myTeam) {
      setError('You are not a member of any team in this department.');
      return;
    }
    setAssignmentMode('manual');
    setTeamId(myTeam.teamId);
    setAssignedToId(user.id);
    setAssignmentExpanded(true);
  }

  const suggestedEnrollmentSource = useMemo(() => {
    if (!enrollmentQuery.trim() || enrollmentRules.length === 0) return null;
    return inferEnrollmentSourceFromRules(enrollmentQuery, enrollmentRules);
  }, [enrollmentQuery, enrollmentRules]);

  const suggestedSourceLabel = useMemo(() => {
    if (!suggestedEnrollmentSource) return null;
    return (
      sourceOptions.find((r) => r.source === suggestedEnrollmentSource)?.label ??
      enrollmentRules.find((r) => r.source === suggestedEnrollmentSource)?.label ??
      null
    );
  }, [suggestedEnrollmentSource, sourceOptions, enrollmentRules]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submittingRef.current) return;
    if (!channelTypeId || !categoryId || !departmentId) {
      setError('No routing rule matches this channel. Add one in Platform Config → Routing.');
      return;
    }
    submittingRef.current = true;
    setLoading(true);
    setError('');
    try {
      const ticket = await createTicket({
        audience,
        categoryId,
        title: title.trim(),
        description: description.trim(),
        channelTypeId,
        linkedSource,
        linkedParentId: selectedParent?.sourceParentId,
        linkedLearnerId: selectedLearner?.sourceLearnerId,
        linkedLearnerName: selectedLearner?.displayName,
        contactName: contactName || undefined,
        contactEmail: contactEmail || undefined,
        contactPhone: contactPhone || undefined,
        assignedToId: assignmentMode === 'manual' ? assignedToId : undefined,
        teamId: assignmentMode === 'manual' ? teamId : undefined,
        priority: TicketPriority.MEDIUM,
      });
      if (pendingFiles.length > 0) {
        try {
          await uploadAssets(ticket.id, pendingFiles);
        } catch (uploadErr) {
          navigate(`/tickets/${ticket.id}`, {
            state: { uploadError: getApiErrorMessage(uploadErr) },
          });
          return;
        }
      }
      navigate(`/tickets/${ticket.id}`);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1>New ticket</h1>
          {hideHostFields && embedSource && (
            <p className="page-subtitle">Creating from {embedSource}</p>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <form className="create-ticket-form" onSubmit={handleSubmit}>
          <div className="create-ticket-form-body">
            <div className="form-grid-triple">
              <div className="form-group">
                <label>Audience</label>
                <input
                  className="form-control"
                  value={TICKET_AUDIENCE_LABELS[audience]}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label>Routed department</label>
                <input
                  className="form-control"
                  value={routedDepartmentLabel}
                  placeholder="Resolved from Routing"
                  readOnly
                />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select
                  className="form-control"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  required
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {!hideHostFields && (
              <div className="form-group form-row-full">
                <label>Channel</label>
                <select
                  className="form-control"
                  value={channelTypeId}
                  onChange={(e) => setChannelTypeId(e.target.value)}
                  required
                >
                  {channelTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group form-row-full">
              <label>Title</label>
              <input
                className="form-control"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                maxLength={500}
              />
            </div>

            <div className="form-group form-row-full form-description">
              <label>Description</label>
              <textarea
                className="form-control"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
              />
            </div>

            {!hideLinkedProfile && (
            <div className="linked-profile-section card">
              <h3 className="linked-profile-heading">
                Linked profile
                <span className="badge badge-neutral optional-badge">Optional</span>
              </h3>
            <div className="form-group">
              <label>Link by</label>
              <div className="form-row" style={{ gap: '1rem' }}>
                <label className="form-check">
                  <input
                    type="radio"
                    name="linkSearchMode"
                    checked={linkSearchMode === 'parent'}
                    onChange={() => handleLinkSearchModeChange('parent')}
                  />
                  Parent name
                </label>
                <label className="form-check">
                  <input
                    type="radio"
                    name="linkSearchMode"
                    checked={linkSearchMode === 'enrollment'}
                    onChange={() => handleLinkSearchModeChange('enrollment')}
                  />
                  Enrollment number
                </label>
              </div>
            </div>
            <div className="form-group">
              <label>Source system</label>
              {hideHostFields ? (
                <input className="form-control" value={embedSource ?? linkedSource} readOnly />
              ) : (
                <select
                  className="form-control"
                  value={linkedSource}
                  onChange={(e) => handleSourceChange(e.target.value as LinkedSource)}
                >
                  {sourceOptions.map((r) => (
                    <option key={r.source} value={r.source}>
                      {r.label}
                    </option>
                  ))}
                </select>
              )}
              {linkSearchMode === 'enrollment' && enrollmentHint && (
                <p className="text-muted" style={{ fontSize: '0.8rem', marginTop: '0.35rem' }}>
                  Select the system first, then search. Known formats: {enrollmentHint}.
                </p>
              )}
              {linkSearchMode === 'enrollment' &&
                suggestedEnrollmentSource &&
                suggestedEnrollmentSource !== linkedSource &&
                suggestedSourceLabel && (
                  <p
                    className="text-muted"
                    style={{ fontSize: '0.8rem', marginTop: '0.35rem', color: 'var(--warning)' }}
                  >
                    This number looks like {suggestedSourceLabel} — consider switching the source
                    before searching.
                  </p>
                )}
            </div>

            {linkSearchMode === 'enrollment' && !selectedParent ? (
              <>
                <div className="search-row">
                  <input
                    className="form-control"
                    placeholder="e.g. 21KE251234"
                    value={enrollmentQuery}
                    onChange={(e) => setEnrollmentQuery(e.target.value.toUpperCase())}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => void handleEnrollmentSearch()}
                    disabled={searchingEnrollment || enrollmentQuery.trim().length < 3}
                  >
                    {searchingEnrollment ? '…' : 'Search'}
                  </button>
                </div>
                {enrollmentResults.length > 0 && (
                  <ul className="entity-search-results">
                    {enrollmentResults.map((match) => (
                      <li key={`${match.source}:${match.learner.sourceLearnerId}`}>
                        <button
                          type="button"
                          className="entity-search-result-btn"
                          onClick={() => selectEnrollmentMatch(match)}
                        >
                          <span>
                            {match.learner.displayName}
                            <span className="entity-search-meta">
                              {' '}
                              · {match.enrollmentNumber}
                            </span>
                          </span>
                          <Badge label={match.source} className={sourceBadgeClass(match.source)} />
                          <span className="entity-search-meta">
                            {match.parent.displayName}
                            {match.learner.schoolName !== '—'
                              ? ` · ${match.learner.schoolName}`
                              : ''}
                            {match.learner.gradeName !== '—'
                              ? ` · ${match.learner.gradeName}`
                              : ''}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : linkSearchMode === 'parent' && !selectedParent ? (
              <>
                <div className="search-row">
                  <input
                    className="form-control"
                    placeholder="Search parents by name…"
                    value={parentQuery}
                    onChange={(e) => setParentQuery(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => void handleParentSearch()}
                    disabled={searchingParents}
                  >
                    {searchingParents ? '…' : 'Search'}
                  </button>
                </div>
                {parentResults.length > 0 && (
                  <ul className="entity-search-results">
                    {parentResults.map((p) => (
                      <li key={p.sourceParentId}>
                        <button
                          type="button"
                          className="entity-search-result-btn"
                          onClick={() => void selectParent(p)}
                        >
                          <span>{p.displayName}</span>
                          <Badge label={p.source} className={sourceBadgeClass(p.source)} />
                          <span className="entity-search-meta">
                            {p.maskedEmail ?? p.maskedPhone ?? '—'}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : selectedParent ? (
              <div className="selected-entity">
                <span>{selectedParent.displayName}</span>
                <Badge
                  label={selectedParent.source}
                  className={sourceBadgeClass(selectedParent.source)}
                />
                <button type="button" className="link-btn" onClick={clearParentSelection}>
                  Change
                </button>
              </div>
            ) : null}

            {selectedParent && (
              <div className="form-group" style={{ marginTop: '0.75rem' }}>
                <label>Learner</label>
                {loadingLearners ? (
                  <p className="text-muted">Loading learners…</p>
                ) : learnerLocked && selectedLearner ? (
                  <div className="selected-entity">
                    <span>
                      {selectedLearner.displayName}
                      {selectedLearner.enrollmentId &&
                      selectedLearner.enrollmentId !== selectedLearner.sourceLearnerId
                        ? ` · ${selectedLearner.enrollmentId}`
                        : ''}
                      {selectedLearner.schoolName !== '—'
                        ? ` · ${selectedLearner.schoolName}`
                        : ''}
                      {selectedLearner.gradeName !== '—'
                        ? ` · ${selectedLearner.gradeName}`
                        : ''}
                    </span>
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => {
                        setLearnerLocked(false);
                        setSelectedLearner(null);
                      }}
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <select
                    className="form-control"
                    value={selectedLearner?.sourceLearnerId ?? ''}
                    onChange={(e) => {
                      const l = learners.find((x) => x.sourceLearnerId === e.target.value);
                      setSelectedLearner(l ?? null);
                    }}
                  >
                    <option value="">Select learner…</option>
                    {learners.map((l) => (
                      <option key={l.sourceLearnerId} value={l.sourceLearnerId}>
                        {l.displayName}
                        {l.schoolName !== '—' ? ` · ${l.schoolName}` : ''}
                        {l.gradeName !== '—' ? ` · ${l.gradeName}` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            <div className="form-row" style={{ marginTop: '0.75rem' }}>
              <div className="form-group">
                <label>Contact name</label>
                <input
                  className="form-control"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Contact email</label>
                <input
                  className="form-control"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Contact phone</label>
                <input
                  className="form-control"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                />
              </div>
            </div>
          </div>
            )}

          <fieldset className="form-fieldset">
            <legend>
              <button
                type="button"
                className="assignment-toggle"
                onClick={() => setAssignmentExpanded((v) => !v)}
              >
                Assignment —{' '}
                {assignmentMode === 'auto'
                  ? 'Auto-assign (recommended)'
                  : 'Manual assignment'}
                <span className="assignment-chevron">{assignmentExpanded ? '▾' : '▸'}</span>
              </button>
            </legend>
            {assignmentExpanded && (
              <div className="assignment-panel">
                <button type="button" className="btn btn-secondary btn-sm" onClick={assignToSelf}>
                  Assign to myself
                </button>
                <div className="form-group">
                  <label>Assign to team member</label>
                  <select
                    className="form-control"
                    value={manualAssignmentValue}
                    onChange={(e) => handleManualAssignmentChange(e.target.value)}
                  >
                    <option value="">Auto-assign (round-robin)</option>
                    {assignableTeams.map((team) =>
                      team.members.map((m) => (
                        <option
                          key={`${team.teamId}:${m.userId}`}
                          value={`${team.teamId}:${m.userId}`}
                        >
                          {team.teamName} › {m.firstName} {m.lastName} ({m.openTicketCount} open)
                        </option>
                      )),
                    )}
                  </select>
                </div>
                {assignmentMode === 'manual' && (
                  <p className="assignment-note">Skipping round-robin assignment</p>
                )}
              </div>
            )}
          </fieldset>

          <div className="form-group">
            <label>Attachments</label>
            <AttachmentPicker
              id="create-ticket-attachments"
              files={pendingFiles}
              onChange={setPendingFiles}
              disabled={loading}
            />
            <p className="form-hint">Optional. Up to 10 MB total per batch.</p>
          </div>
          </div>

          <div className="form-sticky-footer">
            <button
              type="submit"
              className="btn btn-accent-submit"
              disabled={loading || !departmentId}
            >
              {loading ? 'Creating…' : 'Create ticket'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
